import { action, computed, observable } from 'mobx'
import { Id } from '@/infra/id'

export interface IncludedItemViewModelProps {
  isIncludedItem: boolean
  isBaseItem: boolean
  inventoryItemId: Id
  inventoryItemVersionId: Id
  name: string
  price: number
  proPrice: number
  description: string
  workspaceId: Id
  includedQuantity: number
  archived: boolean
  /**
   * Called when the item is toggled in or out of the bundle.
   */
  onIncludedChange?: (item: IncludedItemViewModel) => void
}

/**
 * A service item that can be included in a bundle.
 */
export class IncludedItemViewModel {
  @observable accessor isIncludedItem = false
  @observable accessor isBaseItem = false
  @observable accessor inventoryItemId: Id = Id.none
  @observable accessor inventoryItemVersionId = Id.none
  @observable accessor name = ''
  @observable accessor price = 0
  @observable accessor proPrice = 0
  @observable accessor description = ''
  @observable accessor workspaceId = Id.none
  @observable accessor includedQuantity = 0
  @observable accessor archived = false

  private readonly _onIncludedChange?: (item: IncludedItemViewModel) => void

  constructor({
    isIncludedItem,
    isBaseItem,
    inventoryItemId,
    inventoryItemVersionId,
    name,
    price,
    proPrice,
    description,
    workspaceId,
    includedQuantity,
    archived,
    onIncludedChange,
  }: IncludedItemViewModelProps) {
    this._onIncludedChange = onIncludedChange
    this.isIncludedItem = isIncludedItem
    this.isBaseItem = isBaseItem
    this.inventoryItemId = inventoryItemId
    this.inventoryItemVersionId = inventoryItemVersionId
    this.name = name
    this.price = price
    this.proPrice = proPrice
    this.description = description
    this.workspaceId = workspaceId
    this.includedQuantity = includedQuantity
    this.archived = archived
  }

  @action.bound
  toggleBaseItem() {
    this.isBaseItem = !this.isBaseItem

    // Also toggle as included item for convenience
    if (!this.isIncludedItem) {
      this.isIncludedItem = !this.isIncludedItem
    }

    if (
      (this.isIncludedItem && this.includedQuantity === 0) ||
      this.isBaseItem
    ) {
      this.includedQuantity = 1
    } else if (!this.isIncludedItem && !this.isBaseItem) {
      this.includedQuantity = 0
    }

    if (this._onIncludedChange) {
      this._onIncludedChange(this)
    }
  }

  @action.bound
  toggleIncludedItem() {
    this.isIncludedItem = !this.isIncludedItem

    // Untoggle base item if included item is untoggled
    if (this.isBaseItem) {
      this.isBaseItem = !this.isBaseItem
    }

    if (this.isIncludedItem && this.includedQuantity === 0) {
      this.includedQuantity = 1
    } else if (!this.isIncludedItem) {
      this.includedQuantity = 0
    }

    if (this._onIncludedChange) {
      this._onIncludedChange(this)
    }
  }

  @action.bound
  setQuantity(value: number | null) {
    this.includedQuantity = value ?? 0
  }

  @computed
  get serviceItemStatusMessage() {
    if (this.archived && this.isBaseItem) {
      return '*archived base item'
    } else if (this.archived) {
      return '*archived'
    } else if (this.isBaseItem) {
      return '*base item'
    }

    return ''
  }
}
